/* eslint-disable @next/next/no-before-interactive-script-outside-document */
import React, { ReactElement, ReactNode, useEffect, useMemo } from "react";
import Head from "next/head";
import Script from "next/script";
import { useRouter } from "next/router";

import FavIcons from "@components/shared/favIcon";
import ClickyScript from "@components/scripts/clickyScript";
import InspectorScript from "@components/scripts/inspectorScript";
import GtmScript from "@components/scripts/gtmScript";
import LeadId from "@components/scripts/leadid";
import TwitterTagScript from "@components/scripts/twitterTagScript";
import MetaPixelScript from "@components/scripts/metaPixel";
import TickTokPixelScript from "@components/scripts/tiktokPixel";

import { useFormContext } from "@hooks/useFormContext";
import { useDomainContext } from "@hooks/useDomainContext";
import useOneSignal from "@hooks/useOneSignal";

import { flushStackedActionsWithBeacon } from "@services/actionsTracker";
import { DomainInfo } from "src/api";
import { Category } from "@lib/shared/types";
import { decodeHtmlEntities } from "@lib/sharedUtils";

export default function DomainWrapper({
    domain,
    pageTitle,
    children,
}: {
    domain: DomainInfo | null;
    pageTitle: string | null;
    children: ReactNode;
}): ReactElement {
    const router = useRouter();

    const {
        clickyCodeId = null,
        metatags = null,
        gtmId = null,
        isInspectorEnabled = null,
        description = null,
        enableTrustedForm = null,
        enableJornayaId = null,
        twitterPixel = null,
    } = domain || {};

    const {
        isBot,
        testConfig,
        visitDetails,
        externalFavicon,
        category,
        loadOneToOneForm,
    } = useDomainContext();

    const { formSubmitted, isSecondServiceSubmitted } = useFormContext();

    const titleText = useMemo(() => {
        return decodeHtmlEntities(
            `${pageTitle ?? ""}${pageTitle ? " | " : ""}${
                domain?.niceName ?? domain?.title ?? ""
            }`,
        );
    }, [pageTitle, domain?.niceName, domain?.title]);

    const isSeveralBrands = domain?.name === "severalbrands.com";

    const secondServiceList = domain?.categories
        ?.find(
            (domainCategory: Category) =>
                domainCategory.slug === category?.slug,
        )
        ?.secondServiceCategories?.map(
            (secondService) => secondService.secondServiceCategory,
        );

    const hasDomainSecondService =
        Array.isArray(secondServiceList) && secondServiceList.length > 0;

    const stopInspector = hasDomainSecondService
        ? isSecondServiceSubmitted
        : formSubmitted;

    /* ===============================
       OneSignal (logic handled internally)
       =============================== */
    useOneSignal();

    /* ===============================
       Flush stacked actions on tab hide
       =============================== */
    useEffect(() => {
        const onHide = () => {
            if (document.visibilityState === "hidden") {
                void flushStackedActionsWithBeacon();
            }
        };

        document.addEventListener("visibilitychange", onHide, {
            passive: true,
        });

        return () => {
            document.removeEventListener("visibilitychange", onHide);
        };
    }, []);

    /* ===============================
       Favicon swap (URL-aware, safe cleanup)
       =============================== */
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const ts = params.get("ts");

        if (!ts?.includes("pm")) return;

        let visibilityHandler: (() => void) | null = null;

        const tryAttach = () => {
            const icons =
                document.querySelectorAll<HTMLLinkElement>("link.favicon");

            if (!icons.length) {
                requestAnimationFrame(tryAttach);
                return;
            }

            const original = icons[0].href;
            const alt =
                "https://dwy9ix7d387oz.cloudfront.net/attachments/2024/1/20da118f-a119-492e-bf66-9592ac880743.png";

            visibilityHandler = () => {
                icons.forEach((icon) => {
                    icon.href =
                        document.visibilityState === "hidden" ? alt : original;
                });
            };

            document.addEventListener("visibilitychange", visibilityHandler);
        };

        tryAttach();

        return () => {
            if (visibilityHandler) {
                document.removeEventListener(
                    "visibilitychange",
                    visibilityHandler,
                );
            }
        };
    }, [router.asPath]);

    const domainCss = domain?.config?.css;
    const testCss = testConfig?.config?.css;

    return (
        <>
            <Head>
                <FavIcons externalFavicon={externalFavicon} />

                {domainCss && (
                    <style
                        id="domain-css"
                        dangerouslySetInnerHTML={{ __html: domainCss }}
                    />
                )}

                {testCss && !testConfig?.config?.isDefault && (
                    <style
                        id="test-css"
                        dangerouslySetInnerHTML={{ __html: testCss }}
                    />
                )}

                {externalFavicon && (
                    <link
                        rel="icon"
                        type="image/x-icon"
                        href={externalFavicon}
                        className="favicon"
                    />
                )}

                {!isSeveralBrands && <title>{titleText}</title>}

                {enableJornayaId && !isBot && <LeadId />}

                {description && (
                    <meta name="description" content={description} />
                )}

                {metatags?.length
                    ? metatags
                          .filter((meta) => !meta.key.startsWith("extra-meta"))
                          .map((item, index) => (
                              <meta
                                  key={index}
                                  name={item.key}
                                  content={item.value}
                              />
                          ))
                    : null}

                {twitterPixel && !isBot && (
                    <TwitterTagScript twitterPixel={twitterPixel} />
                )}

                {!isBot &&
                    domain?.advertisingNetworkTrackingSettings?.pixelId &&
                    domain?.advertisingNetworkTrackingSettings?.network ===
                        "facebook" && (
                        <MetaPixelScript
                            externalId={visitDetails?.visitId}
                            pixelId={
                                domain.advertisingNetworkTrackingSettings
                                    .pixelId
                            }
                        />
                    )}

                {!isBot &&
                    domain?.advertisingNetworkTrackingSettings?.pixelId &&
                    domain?.advertisingNetworkTrackingSettings?.network ===
                        "tiktok" && (
                        <TickTokPixelScript
                            hashedExternalId={
                                visitDetails?.hashedVisitId as string
                            }
                            pixelId={
                                domain.advertisingNetworkTrackingSettings
                                    .pixelId
                            }
                        />
                    )}
            </Head>
            {gtmId &&
                // router.asPath !== "/do-not-sell" &&
                (!isBot || Boolean(router.query.gclid)) && (
                    <GtmScript gtmId={gtmId} />
                )}
            {enableTrustedForm && !isBot && (
                <Script
                    id="trustedFormsScript"
                    strategy="lazyOnload"
                    src={`https://cdn.trustedform.com/bootstrap.js?${
                        loadOneToOneForm ? "use_tagged_consent=true&" : ""
                    }field=TrustedFormCertUrl&provide_referrer=true&load_async=true&l=${Date.now()}${Math.random()}`}
                />
            )}
            {clickyCodeId && !isBot && <ClickyScript id={clickyCodeId} />}
            {isInspectorEnabled && !isBot && (
                <InspectorScript stopInterval={stopInspector} />
            )}
            {children}
        </>
    );
}
